import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { CertificateReminderCard } from "@/components/CertificateReminderCard";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, Trophy, TrendingUp, Calendar, BarChart3, Download, History, X, Award } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/useConfirm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function ProgressPage() {
  const [scores, setScores] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("by-learner");
  const { confirm, ConfirmDialog } = useConfirm();
  const [timelineLearner, setTimelineLearner] = useState(null);
  const [timeline, setTimeline] = useState([]);
  // learner_key -> grant object (certificate unlocked without quiz passes)
  const [grants, setGrants] = useState({});

  const normKey = (name) => (name || "").trim().split(/\s+/).join(" ").toLowerCase();

  const loadGrants = async () => {
    try {
      const { data } = await api.get("/program-certificate/grants");
      const map = {};
      (data.grants || []).forEach((g) => { map[g.learner_key] = g; });
      setGrants(map);
    } catch { /* older backend */ }
  };

  const grantCert = async (learnerName) => {
    const ok = await confirm({
      title: `Grant certificate to ${learnerName}?`,
      description: "Their Certificate tab unlocks immediately — no quiz passes required.",
      confirmLabel: "Grant",
    });
    if (!ok) return;
    try {
      await api.post("/program-certificate/grants", { learner_name: learnerName });
      toast.success(`Certificate granted to ${learnerName}`);
      loadGrants();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't grant certificate");
    }
  };

  const revokeCert = async (learnerName) => {
    const ok = await confirm({
      title: `Revoke certificate access for ${learnerName}?`,
      description: "They'll need to pass all module quizzes to unlock it again. Already-generated certificates stay valid.",
      confirmLabel: "Revoke",
    });
    if (!ok) return;
    try {
      await api.delete(`/program-certificate/grants/${encodeURIComponent(normKey(learnerName))}`);
      toast.success("Grant revoked");
      loadGrants();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revoke grant");
    }
  };

  const openTimeline = async (learnerName) => {
    setTimelineLearner(learnerName);
    try {
      const { data } = await api.get(`/progress/timeline`, { params: { learner_name: learnerName } });
      setTimeline(data);
    } catch {
      setTimeline([]);
    }
  };

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/progress"),
      api.get("/progress/by-learner"),
    ]);
    setScores(a.data);
    setSummary(b.data);
  };
  useEffect(() => { load(); }, []);

  const removeScore = async (id) => {
    const ok = await confirm({
      title: "Delete this score record?",
      description: "The score will be permanently removed.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/progress/${id}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = () => {
    const headers = ["Learner", "Email", "Module", "Course", "Score", "Max", "Percentage", "Completed At"];
    const rows = scores.map((s) => [
      s.learner_name,
      s.learner_email || "",
      s.module_title,
      s.course_title,
      s.score ?? "",
      s.max_score ?? "",
      s.percentage ?? "",
      s.completed_at,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `univicoustic-scores-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    toast.success("Exported");
  };

  const filteredScores = useMemo(
    () => scores.filter((s) =>
      !filter.trim() ||
      s.learner_name.toLowerCase().includes(filter.toLowerCase()) ||
      (s.module_title || "").toLowerCase().includes(filter.toLowerCase())),
    [scores, filter]
  );
  const filteredSummary = useMemo(
    () => summary.filter((s) => !filter.trim() || s.learner_name.toLowerCase().includes(filter.toLowerCase())),
    [summary, filter]
  );

  const overall = useMemo(() => {
    if (scores.length === 0) return { total: 0, avg: 0, learners: 0 };
    const withPct = scores.filter((s) => s.percentage != null);
    const avg = withPct.length ? withPct.reduce((a, b) => a + b.percentage, 0) / withPct.length : 0;
    const uniqueLearners = new Set(scores.map((s) => s.learner_name)).size;
    return { total: scores.length, avg: Math.round(avg), learners: uniqueLearners };
  }, [scores]);

  return (
    <AdminLayout>
      <div className="px-10 py-10 max-w-7xl">
        <header className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Analytics</div>
            <h1 className="text-4xl font-display font-bold text-slate-900 tracking-tight">Learner Progress</h1>
            <p className="text-slate-500 mt-2">Quiz scores and completions reported by modules in real-time.</p>
          </div>
          <button
            data-testid="export-csv-button"
            onClick={exportCsv}
            disabled={scores.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:border-slate-900 disabled:opacity-50"
          >
            <Download size={14}/> Export CSV
          </button>
        </header>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="p-5 border-slate-200" data-testid="stat-total-scores">
            <div className="inline-flex p-2 rounded-md bg-blue-50 text-blue-900 mb-3"><Trophy size={18}/></div>
            <div className="text-3xl font-display font-bold text-slate-900">{overall.total}</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Total Attempts</div>
          </Card>
          <Card className="p-5 border-slate-200" data-testid="stat-avg-score">
            <div className="inline-flex p-2 rounded-md bg-amber-50 text-amber-700 mb-3"><TrendingUp size={18}/></div>
            <div className="text-3xl font-display font-bold text-slate-900">{overall.avg}%</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Average Score</div>
          </Card>
          <Card className="p-5 border-slate-200" data-testid="stat-active-learners">
            <div className="inline-flex p-2 rounded-md bg-emerald-50 text-emerald-700 mb-3"><BarChart3 size={18}/></div>
            <div className="text-3xl font-display font-bold text-slate-900">{overall.learners}</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Active Learners</div>
          </Card>
        </div>

        <CertificateReminderCard />

        <Input
          data-testid="progress-filter-input"
          placeholder="Filter by learner or module…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-4 max-w-md"
        />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="by-learner" data-testid="tab-by-learner">By Learner</TabsTrigger>
            <TabsTrigger value="all-attempts" data-testid="tab-all-attempts">All Attempts</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="by-learner">
            {filteredSummary.length === 0 ? (
              <Card className="p-16 text-center border-dashed">
                <Trophy size={36} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3}/>
                <div className="text-slate-700 font-medium">No progress recorded yet</div>
                <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                  When learners complete a module, their scores will appear here automatically.
                </div>
              </Card>
            ) : (
              <Card className="border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Learner</th>
                      <th className="px-5 py-3 font-semibold">Modules Completed</th>
                      <th className="px-5 py-3 font-semibold">Attempts</th>
                      <th className="px-5 py-3 font-semibold">Average Score</th>
                      <th className="px-5 py-3 font-semibold">Certificate</th>
                      <th className="px-5 py-3 font-semibold">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSummary.map((s) => (
                      <tr key={`${s.learner_id || s.learner_name}`} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => openTimeline(s.learner_name)} data-testid={`summary-row-${s.learner_name}`}>
                        <td className="px-5 py-3 text-sm font-medium text-slate-900 flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-[#222222] text-white flex items-center justify-center text-xs font-semibold">
                            {s.learner_name?.charAt(0).toUpperCase()}
                          </span>
                          {s.learner_name}
                        </td>
                        <td className="px-5 py-3 text-sm text-slate-700">{s.modules_completed_count}</td>
                        <td className="px-5 py-3 text-sm text-slate-700">{s.attempts}</td>
                        <td className="px-5 py-3 text-sm font-medium">
                          {s.avg_percentage != null ? (
                            <ScoreBar value={s.avg_percentage} />
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-5 py-3 text-sm">
                          {grants[normKey(s.learner_name)] ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-[11px] font-semibold" data-testid={`cert-granted-badge-${s.learner_name}`}>
                                <Award size={11}/> Granted
                              </span>
                              {!grants[normKey(s.learner_name)].built_in && (
                                <button
                                  data-testid={`cert-revoke-button-${s.learner_name}`}
                                  onClick={(e) => { e.stopPropagation(); revokeCert(s.learner_name); }}
                                  title="Revoke certificate access"
                                  className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                                >
                                  <X size={13}/>
                                </button>
                              )}
                            </span>
                          ) : (
                            <button
                              data-testid={`cert-grant-button-${s.learner_name}`}
                              onClick={(e) => { e.stopPropagation(); grantCert(s.learner_name); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 border border-slate-300 text-slate-600 rounded-full text-[11px] font-semibold hover:border-[#E53935] hover:text-[#E53935] transition-colors"
                            >
                              <Award size={11}/> Grant
                            </button>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">
                          <div className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5"><Calendar size={11}/> {s.last_activity ? new Date(s.last_activity).toLocaleString() : "—"}</span>
                            <span className="text-[#E53935] inline-flex items-center gap-1 font-medium"><History size={11}/> Timeline</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="all-attempts">
            {filteredScores.length === 0 ? (
              <Card className="p-16 text-center border-dashed">
                <div className="text-slate-700 font-medium">No score records</div>
              </Card>
            ) : (
              <Card className="border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Learner</th>
                      <th className="px-5 py-3 font-semibold">Module</th>
                      <th className="px-5 py-3 font-semibold">Course</th>
                      <th className="px-5 py-3 font-semibold">Score</th>
                      <th className="px-5 py-3 font-semibold">When</th>
                      <th className="px-5 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredScores.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`score-row-${s.id}`}>
                        <td className="px-5 py-3 text-sm text-slate-900 font-medium">{s.learner_name}</td>
                        <td className="px-5 py-3 text-sm text-slate-700">{s.module_title}</td>
                        <td className="px-5 py-3 text-sm text-slate-500">{s.course_title}</td>
                        <td className="px-5 py-3 text-sm">
                          {s.percentage != null ? (
                            <div className="flex items-center gap-2">
                              <ScoreBar value={s.percentage} />
                              {s.score != null && s.max_score != null && (
                                <span className="text-xs text-slate-500 font-mono">{s.score}/{s.max_score}</span>
                              )}
                            </div>
                          ) : <span className="text-emerald-700 text-xs font-medium">Completed</span>}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{new Date(s.completed_at).toLocaleString()}</td>
                        <td className="px-5 py-3">
                          <button onClick={() => removeScore(s.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-score-${s.id}`}>
                            <Trash2 size={13}/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <ConfirmDialog />

      {/* Per-attempt timeline modal */}
      <Dialog open={!!timelineLearner} onOpenChange={(v) => { if (!v) { setTimelineLearner(null); setTimeline([]); }}}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <History size={16} className="text-[#E53935]"/> Attempt Timeline · {timelineLearner}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto thin-scroll pr-2 -mr-2">
            {timeline.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-8">No attempts recorded.</div>
            ) : (
              <div className="space-y-2.5">
                {timeline.map((t) => (
                  <div key={t.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid={`timeline-attempt-${t.id}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                      t.is_best ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                    }`}>#{t.attempt_number}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{t.module_title}</div>
                      <div className="text-xs text-slate-500">{t.course_title || "—"} · {new Date(t.completed_at).toLocaleString()}</div>
                    </div>
                    <div className="text-right shrink-0">
                      {t.percentage != null ? (
                        <>
                          <div className={`text-lg font-display font-bold ${
                            t.percentage >= 80 ? "text-emerald-600" :
                            t.percentage >= 60 ? "text-amber-600" : "text-red-600"
                          }`}>{t.percentage.toFixed(0)}%</div>
                          <div className="text-[10px] text-slate-500">{t.score}/{t.max_score}</div>
                        </>
                      ) : (
                        <div className="text-xs text-emerald-700 font-medium">Completed</div>
                      )}
                      {t.is_best && <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold mt-0.5">★ Best</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function ScoreBar({ value }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="inline-flex items-center gap-2 min-w-[140px]">
      <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}/>
      </div>
      <span className="text-xs font-mono text-slate-700">{pct.toFixed(0)}%</span>
    </div>
  );
}
